import React, { useState, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import { RuleEngine } from '../core/logic/RuleEngine';
import type { RuleEffects } from '../core/logic/RuleEngine';
import { useDynamicDefinition } from '../core/hooks/useDynamicDefinition';
import { DynamicFieldRenderer } from './DynamicFieldRenderer';
import { useDynamicEntitiesApi } from '../core/hooks/useDynamicEntitiesApi';
import { isDynamicEntitiesHttpError } from '../core/api/DynamicEntitiesHttpError';
import { useDynamicEntitiesContext } from '../core/DynamicEntitiesProvider';
import { DynamicFieldType } from '../core/types/DynamicField.types';
import type { DynamicField } from '../core/types/DynamicField.types';


export interface DynamicEntitiesProps {
    featureCode: string;
    entityCode: string;
    recordId?: string;
    idEntityMultiple?: string;
    onSaved?: () => void;
    className?: string;
    hideSubmitButton?: boolean;
    hideTitle?: boolean;
    /**
     * Avisa si la entidad tiene algo que pintar, en cuanto resuelve su definición y cada vez que
     * las reglas cambian qué campos son visibles.
     *
     * Existe para que el ANFITRIÓN pueda esconder su propio marco. Una entidad recién creada a la
     * que todavía no le pusieron campos devuelve una definición válida con `fields: []`, y sin este
     * aviso el anfitrión dibujaba una tarjeta con título y nada dentro. La definición se pide aquí
     * de todos modos, así que enterarse por este camino no cuesta una petición extra — resolverlo
     * en el anfitrión sí la costaría, porque `useDynamicDefinition` no cachea.
     */
    onRenderableChange?: (renderable: boolean) => void;
}

export interface DynamicEntitiesRef {
    save: (overrideRecordId?: string) => Promise<boolean>;
}

/**
 * Rellena con `false` los interruptores y casillas que no traen valor.
 *
 * Un interruptor apagado ES un valor, no una ausencia. El formulario solo envía las claves que
 * tiene en estado, así que guardar un registro sin tocar el campo no escribía ninguna fila: el
 * maestro se quedaba sin el dato, y filtrar «igual a No» no encontraba ese registro hasta que
 * alguien lo encendía y lo apagaba. Los demás tipos no llevan valor por omisión —un texto vacío no
 * es un dato— y por eso solo se siembran estos dos.
 *
 * Nunca pisa una clave existente: al editar, lo que llega del servidor manda.
 */
const withBooleanDefaults = (
    fields: DynamicField[] | undefined,
    values: Record<string, any>,
): Record<string, any> => {
    const seeded = { ...values };

    (fields ?? []).forEach(field => {
        const isBoolean =
            field.type === DynamicFieldType.Checkbox || field.type === DynamicFieldType.Switch;

        if (isBoolean && field.visible && seeded[field.code] === undefined) {
            seeded[field.code] = false;
        }
    });

    return seeded;
};

export const DynamicEntities = React.forwardRef<DynamicEntitiesRef, DynamicEntitiesProps>(({
    featureCode,
    entityCode,
    recordId,
    idEntityMultiple,
    onSaved,
    className,
    hideSubmitButton,
    hideTitle,
    onRenderableChange
}, ref) => {
    const { onError } = useDynamicEntitiesContext();
    const api = useDynamicEntitiesApi();
    const { definition, loading, error } = useDynamicDefinition(featureCode, entityCode);

    const [values, setValues] = useState<Record<string, any>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    // Referencia para evitar cierres obsoletos en la función save()
    const valuesRef = useRef(values);

    useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    // La siembra va aquí y no en el `useState` porque la definición llega después del primer
    // render: hasta que no se sabe qué campos hay, no se sabe cuáles son booleanos.
    useEffect(() => {
        if (!definition) return;
        setValues(prev => withBooleanDefaults(definition.fields, prev));
    }, [definition]);

    useEffect(() => {
        if (recordId && !loading && definition) {
            // For multi-record creation, if idEntityMultiple is provided but it's new, we shouldn't fetch unless we know it exists.
            // But usually we just attempt fetch.
            api.getValues(featureCode, entityCode, recordId, idEntityMultiple)
                .then(data => setValues(withBooleanDefaults(definition.fields, data.values)))
                .catch(err => {
                    // Un 404 es esperable: el registro todavía no tiene valores
                    // EAV. Cualquier otro fallo —401 el primero— sí tiene que
                    // llegar al anfitrión en vez de quedarse en la consola.
                    if (isDynamicEntitiesHttpError(err) && err.status === 404) {
                        console.warn("Could not fetch values, assuming new record", err);
                        return;
                    }
                    console.error("Could not fetch values", err);
                    onError?.(err as Error);
                });
        }
    }, [recordId, idEntityMultiple, loading, definition, api, featureCode, entityCode]);

    // Rule Engine Integration
    const ruleEffects: RuleEffects = useMemo(() => {
        if (!definition || !definition.rules || definition.rules.length === 0) {
            return { hiddenFields: new Set(), disabledFields: new Set(), requiredFields: new Set() };
        }
        return RuleEngine.evaluate(definition, values);
    }, [definition, values]);

    // Los campos que realmente se pintan: los visibles por definición y no ocultos por una regla.
    // Se calcula aquí, y no dentro del render, porque de él depende también si la entidad tiene algo
    // que mostrar.
    const visibleFields = useMemo(
        () => (definition?.fields ?? []).filter(f => f.visible && !ruleEffects.hiddenFields.has(f.code)),
        [definition, ruleEffects],
    );

    // Se avisa solo cuando la definición ya resolvió: mientras carga no se sabe, y decir "no hay
    // nada" en ese momento haría parpadear el marco del anfitrión.
    useEffect(() => {
        if (!loading && definition) onRenderableChange?.(visibleFields.length > 0);
    }, [loading, definition, visibleFields.length, onRenderableChange]);

    const validate = (valuesToValidate: Record<string, any> = values) => {
        if (!definition) return true;
        const newErrors: Record<string, string> = {};

        definition.fields.forEach(field => {
            const val = valuesToValidate[field.code];
            // Apply Rule Effects
            if (ruleEffects.hiddenFields.has(field.code)) return; // Don't validate hidden fields

            if (!field.visible) return; // Base visibility
            if (field.disabled || ruleEffects.disabledFields.has(field.code)) return; // Don't validate disabled fields? Usually yes.

            const isRequired = field.required || ruleEffects.requiredFields.has(field.code);

            // Required check
            if (isRequired && (val === undefined || val === null || val === '')) {
                newErrors[field.code] = 'Este campo es obligatorio';
            }

            // Regex check
            if (val && field.regex) {
                try {
                    const regex = new RegExp(field.regex);
                    if (!regex.test(String(val))) {
                        newErrors[field.code] = 'El formato no es válido';
                    }
                } catch (e) {
                    console.warn(`Invalid regex for field ${field.code}`, field.regex);
                }
            }

            // Min/Max check (Numbers & Currency)
            if (val !== undefined && val !== null &&
                (field.type === 'NumberInteger' || field.type === 'NumberDecimal' || field.type === 'Currency')) {
                const num = Number(val);
                if (field.minValue !== undefined && field.minValue !== null && num < field.minValue) {
                    newErrors[field.code] = `El valor mínimo es ${field.minValue}`;
                }
                if (field.maxValue !== undefined && field.maxValue !== null && num > field.maxValue) {
                    newErrors[field.code] = `El valor máximo es ${field.maxValue}`;
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const save = async (overrideRecordId?: string): Promise<boolean> => {
        const currentValues = valuesRef.current;

        if (!validate(currentValues)) return false;

        const finalRecordId = overrideRecordId || recordId;
        if (!finalRecordId) {
            console.error("No record ID provided for dynamic entities save");
            return false;
        }

        setSaving(true);
        try {
            await api.saveValues(featureCode, entityCode, finalRecordId, { values: currentValues }, idEntityMultiple);
            if (onSaved) onSaved();
            return true;
        } catch (err) {
            if (onError) onError(err as Error);
            return false;
        } finally {
            setSaving(false);
        }
    };

    useImperativeHandle(ref, () => ({
        save
    }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await save();
    };

    if (loading) return <div>Cargando definición...</div>;
    if (error) return <div>Error cargando la definición: {error.message}</div>;
    if (!definition) return <div>No se encontró definición.</div>;

    // Sin fondo propio: el formulario hereda el del contenedor que lo monta. Llevaba `bg-white`
    // fijo —sin variante oscura—, así que en modo oscuro pintaba un rectángulo blanco encima de la
    // tarjeta del anfitrión, y en modo claro dibujaba una segunda tarjeta dentro de la que ya había.
    // Los tres sitios que lo montan (admin, orquestador y MasterDocument) aportan su propio fondo.
    return (
        <div className={`space-y-4 p-0 rounded-lg ${className || ''}`}>
            {!hideTitle && (
                <h2 className="text-xl font-bold mb-4">{definition.description || definition.entityCode}</h2>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {visibleFields
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map(field => {
                        // Resolve Options from Definition Lists
                        let options: { label: string, value: string | number }[] = [];
                        if (field.genericListId) {
                            const list = definition.lists?.find(l => l.id === field.genericListId);
                            if (list) {
                                options = list.items
                                    .sort((a, b) => a.order - b.order)
                                    .map(item => ({ label: item.label, value: item.value }));
                            }
                        }

                        const colSpan = field.layouts?.find(l => l.breakpoint === 'md')?.colSpan || 1;
                        const isFullWidthDefault = field.type === 'TextArea';
                        const spanClass = isFullWidthDefault ? 'col-span-full md:col-span-full' :
                            colSpan === 2 ? 'col-span-1 md:col-span-2' :
                                colSpan === 3 ? 'col-span-1 md:col-span-3' :
                                    colSpan === 4 ? 'col-span-1 md:col-span-4' :
                                        'col-span-1 md:col-span-1';

                        return (
                            <div key={field.id} className={spanClass}>
                                <DynamicFieldRenderer
                                    field={{
                                        ...field,
                                        disabled: field.disabled || ruleEffects.disabledFields.has(field.code),
                                        required: field.required || ruleEffects.requiredFields.has(field.code)
                                    }}
                                    value={values[field.code]}
                                    error={errors[field.code]}
                                    onChange={(val) => {
                                        setValues(prev => ({ ...prev, [field.code]: val }));
                                        // Clear error on change
                                        if (errors[field.code]) setErrors(prev => ({ ...prev, [field.code]: '' }));
                                    }}
                                    options={options}
                                />
                            </div>
                        );
                    })}
            </div>

            {!hideSubmitButton && (
                <div className="flex justify-end pt-4">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        {saving ? 'Guardando...' : 'Guardar Registro'}
                    </button>
                </div>
            )}
        </div>
    );
});
